import {
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  GripVertical,
  Image as ImageIcon,
  Laptop,
  LayoutGrid,
  LayoutTemplate,
  LibraryBig,
  Loader2,
  LockKeyhole,
  Monitor,
  Palette,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Smartphone,
  Sparkles,
  Tablet,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, DragEvent, MouseEvent } from "react";
import { PageHeader } from "../../components/ui/PageHeader";
import { cn } from "../../lib/cn";
import { apiFetch } from "../../lib/session";
import { useAccount } from "../../context/AccountContext";
import {
  builderTemplates,
  sectionCatalog,
  type BuilderSection,
  type SectionDefinition,
  type TemplatePreset,
} from "../../data/builderCatalog";

type Viewport = "desktop" | "tablet" | "mobile";
type Site = {
  websiteId: string;
  revision: number;
  slug: string;
  previewToken: string;
  status: string;
  business: { name: string; description: string; whatsapp: string; address?: string };
  theme?: { id: string; version: string };
  sections: BuilderSection[];
  products: Array<Record<string, any>>;
  [key: string]: any;
};

type FieldConfig = {
  key: string;
  label: string;
  type?: "input" | "textarea";
  placeholder?: string;
};

const legacyLabels: Record<string, string> = {
  navbar: "Navbar",
  contact: "Kontak",
};

const themeChoices = [
  { id: "culinary-fresh", label: "Kuliner Fresh", description: "Hijau segar dan oranye hangat", swatches: ["#16a34a", "#f97316", "#fffaf5"] },
  { id: "craft-natural", label: "Craft Natural", description: "Natural, hangat, dan premium", swatches: ["#3f7d58", "#d97706", "#fbfaf6"] },
  { id: "export-professional", label: "Export Professional", description: "Teal profesional untuk buyer global", swatches: ["#0f766e", "#ea580c", "#f8fafc"] },
];

const fieldMap: Record<string, FieldConfig[]> = {
  hero: [
    { key: "kicker", label: "Label kecil", placeholder: "Produk lokal pilihan" },
    { key: "headline", label: "Headline", type: "textarea" },
    { key: "subheadline", label: "Subheadline", type: "textarea" },
    { key: "ctaText", label: "Teks tombol utama" },
  ],
  about: [
    { key: "kicker", label: "Label kecil" },
    { key: "headline", label: "Judul", type: "textarea" },
    { key: "subheadline", label: "Cerita usaha", type: "textarea" },
    { key: "statOneValue", label: "Angka pertama" },
    { key: "statOneLabel", label: "Keterangan pertama" },
    { key: "statTwoValue", label: "Angka kedua" },
    { key: "statTwoLabel", label: "Keterangan kedua" },
  ],
  products: [
    { key: "kicker", label: "Label kecil" },
    { key: "headline", label: "Judul section" },
    { key: "subheadline", label: "Deskripsi", type: "textarea" },
  ],
  benefits: [
    { key: "kicker", label: "Label kecil" },
    { key: "headline", label: "Judul section" },
    { key: "subheadline", label: "Deskripsi", type: "textarea" },
  ],
  stats: [
    { key: "kicker", label: "Label kecil" },
    { key: "headline", label: "Judul section" },
    { key: "itemOneValue", label: "Nilai 1" },
    { key: "itemOneLabel", label: "Label 1" },
    { key: "itemTwoValue", label: "Nilai 2" },
    { key: "itemTwoLabel", label: "Label 2" },
    { key: "itemThreeValue", label: "Nilai 3" },
    { key: "itemThreeLabel", label: "Label 3" },
    { key: "itemFourValue", label: "Nilai 4" },
    { key: "itemFourLabel", label: "Label 4" },
  ],
  process: [
    { key: "kicker", label: "Label kecil" },
    { key: "headline", label: "Judul section" },
    { key: "stepOneTitle", label: "Judul langkah 1" },
    { key: "stepOneText", label: "Deskripsi langkah 1", type: "textarea" },
    { key: "stepTwoTitle", label: "Judul langkah 2" },
    { key: "stepTwoText", label: "Deskripsi langkah 2", type: "textarea" },
    { key: "stepThreeTitle", label: "Judul langkah 3" },
    { key: "stepThreeText", label: "Deskripsi langkah 3", type: "textarea" },
  ],
  gallery: [
    { key: "kicker", label: "Label kecil" },
    { key: "headline", label: "Judul section" },
    { key: "subheadline", label: "Deskripsi", type: "textarea" },
  ],
  testimonials: [
    { key: "kicker", label: "Label kecil" },
    { key: "headline", label: "Judul section" },
    { key: "subheadline", label: "Deskripsi", type: "textarea" },
  ],
  faq: [
    { key: "kicker", label: "Label kecil" },
    { key: "headline", label: "Judul section" },
    { key: "subheadline", label: "Deskripsi", type: "textarea" },
  ],
  cta: [
    { key: "kicker", label: "Label kecil" },
    { key: "headline", label: "Judul CTA", type: "textarea" },
    { key: "subheadline", label: "Deskripsi CTA", type: "textarea" },
    { key: "ctaText", label: "Teks tombol" },
  ],
  "export-profile": [
    { key: "kicker", label: "Label kecil" },
    { key: "headline", label: "Nama produk / judul section" },
    { key: "subheadline", label: "Deskripsi buyer", type: "textarea" },
    { key: "hsCode", label: "HS code" },
    { key: "moq", label: "MOQ" },
    { key: "capacity", label: "Kapasitas" },
    { key: "leadTime", label: "Lead time" },
    { key: "packaging", label: "Packaging" },
    { key: "privateLabel", label: "Private label" },
    { key: "incoterm", label: "Incoterm" },
    { key: "language", label: "Bahasa katalog" },
    { key: "targetMarket", label: "Target pasar" },
  ],
};

function createSection(definition: SectionDefinition): BuilderSection {
  return {
    id: `${definition.type}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    type: definition.type,
    variant: definition.variants[0]?.id || "default",
    enabled: true,
    settings: { ...definition.defaultSettings },
  };
}

function sectionLabel(type: string) {
  return sectionCatalog.find((item) => item.type === type)?.label || legacyLabels[type] || type;
}

export function BuilderPage() {
  const { account, refresh } = useAccount();
  const [site, setSite] = useState<Site | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [previewKey, setPreviewKey] = useState(0);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [libraryQuery, setLibraryQuery] = useState("");
  const [libraryCategory, setLibraryCategory] = useState("Semua");
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const sitesBase = (import.meta.env.VITE_SITES_BASE_URL || "http://localhost:8788").replace(/\/$/, "");

  const loadSite = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await apiFetch<{ site: Site }>("/api/dashboard/site");
      setSite(result.site);
      setSelectedId((current) => current || result.site.sections.find((item) => item.type === "hero")?.id || result.site.sections[0]?.id || "");
      setPreviewKey((value) => value + 1);
      setDirty(false);
    } catch {
      setError("Website belum berhasil dimuat. Pastikan session dashboard masih aktif.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadSite(); }, []);

  const sections = site?.sections || [];
  const selected = useMemo(() => sections.find((section) => section.id === selectedId) ?? sections[0], [sections, selectedId]);
  const selectedDefinition = useMemo(() => sectionCatalog.find((item) => item.type === selected?.type), [selected?.type]);
  const previewUrl = site ? `${sitesBase}/preview/${site.previewToken}?v=${site.revision}-${previewKey}` : sitesBase;

  const updateSite = (updater: (current: Site) => Site) => {
    setSite((current) => current ? updater(current) : current);
    setDirty(true);
  };

  const updateSections = (next: BuilderSection[]) => updateSite((current) => ({ ...current, sections: next }));

  const move = (id: string, direction: -1 | 1) => {
    const index = sections.findIndex((section) => section.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[index], next[target]] = [next[target], next[index]];
    updateSections(next);
  };

  const moveTo = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    const sourceIndex = sections.findIndex((item) => item.id === sourceId);
    const targetIndex = sections.findIndex((item) => item.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const next = [...sections];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    updateSections(next);
  };

  const toggle = (id: string) => updateSections(sections.map((section) => section.id === id ? { ...section, enabled: !section.enabled } : section));

  const updateSelected = (patch: Partial<BuilderSection>, settingsPatch?: Record<string, unknown>) => {
    updateSections(sections.map((section) => section.id === selectedId ? {
      ...section,
      ...patch,
      settings: settingsPatch ? { ...(section.settings || {}), ...settingsPatch } : section.settings,
    } : section));
  };

  const addSection = (definition: SectionDefinition) => {
    if (definition.exportOnly && !account?.entitlements.exportTools) {
      setError("Section Profil Ekspor tersedia setelah paket Siap Export aktif.");
      return;
    }
    const next = createSection(definition);
    const footerIndex = sections.findIndex((item) => item.type === "footer");
    const insertAt = footerIndex >= 0 ? footerIndex : sections.length;
    const updated = [...sections];
    updated.splice(insertAt, 0, next);
    updateSections(updated);
    setSelectedId(next.id);
    setLibraryOpen(false);
    setNotice(`${definition.label} ditambahkan. Atur kontennya di panel kanan.`);
  };

  const duplicateSelected = () => {
    if (!selected) return;
    const index = sections.findIndex((item) => item.id === selected.id);
    const duplicate = { ...selected, id: `${selected.type}_${Date.now()}`, settings: { ...selected.settings } };
    const next = [...sections];
    next.splice(index + 1, 0, duplicate);
    updateSections(next);
    setSelectedId(duplicate.id);
    setNotice(`${sectionLabel(selected.type)} berhasil diduplikasi.`);
  };

  const deleteSelected = () => {
    if (!selected || selected.type === "hero" || selected.type === "footer") {
      setError("Hero dan Footer dipertahankan agar struktur halaman tetap utuh. Section dapat dinonaktifkan melalui toggle.");
      return;
    }
    const index = sections.findIndex((item) => item.id === selected.id);
    const next = sections.filter((item) => item.id !== selected.id);
    updateSections(next);
    setSelectedId(next[Math.max(0, index - 1)]?.id || next[0]?.id || "");
    setNotice(`${sectionLabel(selected.type)} dihapus dari halaman.`);
  };

  const applyTemplate = (template: TemplatePreset) => {
    if (template.exportOnly && !account?.entitlements.exportTools) {
      setError("Template Export B2B hanya tersedia untuk paket Siap Export.");
      return;
    }
    const nextSections = template.sections.map((item, index) => ({
      ...item,
      id: `${item.type}_${Date.now()}_${index}`,
      settings: { ...item.settings },
    }));
    updateSite((current) => ({
      ...current,
      theme: { id: template.themeId, version: "1.0.0" },
      sections: nextSections,
    }));
    setSelectedId(nextSections[0]?.id || "");
    setTemplateOpen(false);
    setNotice(`Template ${template.name} diterapkan. Produk dan data usaha tetap dipertahankan.`);
  };

  const changeTheme = (themeId: string) => {
    updateSite((current) => ({ ...current, theme: { id: themeId, version: "1.0.0" } }));
  };

  const handleHeroImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !site) return;
    if (!file.type.startsWith("image/")) {
      setError("Pilih file gambar JPG, PNG, atau WebP.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const imageUrl = String(reader.result || "");
      updateSelected({}, { imageUrl });
      setNotice("Gambar hero diperbarui. Simpan draft untuk menyinkronkan preview.");
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const save = async (): Promise<boolean> => {
    if (!site) return false;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const result = await apiFetch<{ site: Site }>("/api/dashboard/site", {
        method: "PUT",
        body: JSON.stringify({ site, expectedRevision: site.revision }),
      });
      setSite(result.site);
      setPreviewKey((value) => value + 1);
      setDirty(false);
      setNotice("Draft tersimpan dan preview telah memakai revision terbaru.");
      await refresh();
      return true;
    } catch (reason) {
      const apiError = reason as Error & { status?: number };
      if (apiError.status === 409) setError("Website berubah melalui WhatsApp atau kanal lain. Muat versi terbaru sebelum menyimpan.");
      else setError("Draft belum berhasil disimpan.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!site) return;
    setPublishing(true);
    setError("");
    setNotice("");
    try {
      const saved = await save();
      if (!saved) return;
      const result = await apiFetch<{ site: Site; url: string }>("/api/dashboard/publish", { method: "POST" });
      setSite(result.site);
      setPreviewKey((value) => value + 1);
      setNotice(`Website dipublikasikan ke ${result.url}`);
      await refresh();
    } catch {
      setError("Website belum berhasil dipublikasikan.");
    } finally {
      setPublishing(false);
    }
  };

  const filteredCatalog = useMemo(() => sectionCatalog.filter((item) => {
    const categoryMatch = libraryCategory === "Semua" || item.category === libraryCategory;
    const query = libraryQuery.trim().toLowerCase();
    const queryMatch = !query || `${item.label} ${item.description}`.toLowerCase().includes(query);
    return categoryMatch && queryMatch;
  }), [libraryCategory, libraryQuery]);

  const widthClass = viewport === "mobile" ? "max-w-[390px]" : viewport === "tablet" ? "max-w-[820px]" : "max-w-full";

  if (loading) return <div className="grid min-h-[640px] place-items-center"><div className="text-center"><Loader2 size={34} className="mx-auto animate-spin text-brand-600" /><p className="mt-3 text-sm font-bold text-slate-500">Memuat builder...</p></div></div>;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="No-code visual builder"
        title="Susun website dari komponen siap pakai"
        description="Pilih template, tarik section, ubah variasi, lalu simpan. Produk dan data usaha tetap tersinkron dengan WhatsApp dan dashboard."
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => setTemplateOpen(true)}><LayoutTemplate size={17} /> Pilih template</button>
            <button type="button" className="btn-secondary" onClick={loadSite}><RefreshCcw size={17} /> Muat terbaru</button>
            <button type="button" className="btn-secondary" onClick={save} disabled={saving || !site}>{saving ? <Loader2 size={17} className="animate-spin" /> : <Save size={17} />} {saving ? "Menyimpan..." : "Simpan draft"}</button>
            <button type="button" className="btn-accent" onClick={publish} disabled={publishing || !site}>{publishing ? <Loader2 size={17} className="animate-spin" /> : <UploadCloud size={17} />} {publishing ? "Publish..." : "Publish"}</button>
          </>
        }
      />

      <section className="grid gap-4 md:grid-cols-3">
        <button type="button" onClick={() => setTemplateOpen(true)} className="group rounded-3xl border border-brand-100 bg-gradient-to-br from-brand-950 to-brand-800 p-5 text-left text-white shadow-soft transition hover:-translate-y-0.5">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-white/10"><LayoutTemplate size={21} /></span>
          <p className="mt-5 text-xs font-black uppercase tracking-[.16em] text-brand-200">Mulai cepat</p>
          <h3 className="mt-1 text-lg font-black">Pakai template lengkap</h3>
          <p className="mt-2 text-xs leading-5 text-white/60">Susunan section dan tema diterapkan dalam satu klik.</p>
        </button>
        <button type="button" onClick={() => setLibraryOpen(true)} className="group rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-soft transition hover:-translate-y-0.5 hover:border-brand-200">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-50 text-brand-700"><LibraryBig size={21} /></span>
          <p className="mt-5 text-xs font-black uppercase tracking-[.16em] text-brand-700">Komponen</p>
          <h3 className="mt-1 text-lg font-black text-slate-900">Tambah section siap pakai</h3>
          <p className="mt-2 text-xs leading-5 text-slate-500">Hero, cerita usaha, statistik, galeri, FAQ, CTA, hingga profil ekspor.</p>
        </button>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-soft">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-accent-50 text-accent-700"><Palette size={21} /></span>
          <p className="mt-5 text-xs font-black uppercase tracking-[.16em] text-accent-700">Tema aktif</p>
          <h3 className="mt-1 text-lg font-black text-slate-900">{themeChoices.find((item) => item.id === site?.theme?.id)?.label || "Tema website"}</h3>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex -space-x-1">{(themeChoices.find((item) => item.id === site?.theme?.id)?.swatches || []).map((color) => <span key={color} className="h-7 w-7 rounded-full border-2 border-white shadow" style={{ background: color }} />)}</div>
            <span className={cn("rounded-full px-3 py-1 text-[10px] font-black", dirty ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700")}>{dirty ? "Belum disimpan" : "Tersimpan"}</span>
          </div>
        </article>
      </section>

      {(notice || error) && <div className={cn("rounded-2xl border p-4 text-sm font-bold", error ? "border-rose-200 bg-rose-50 text-rose-700" : "border-brand-100 bg-brand-50 text-brand-800")}>{error || notice}</div>}

      <div className="grid min-h-[780px] overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-soft xl:grid-cols-[280px_minmax(0,1fr)_340px]">
        <aside className="border-b border-slate-200 bg-slate-50/70 p-4 xl:border-b-0 xl:border-r">
          <div className="flex items-center justify-between px-2">
            <div><p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">Struktur halaman</p><p className="mt-1 text-xs text-slate-400">Tarik untuk mengubah urutan</p></div>
            <button type="button" onClick={() => setLibraryOpen(true)} className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 bg-white text-brand-700" aria-label="Tambah section"><Plus size={17} /></button>
          </div>
          <div className="mt-4 space-y-2">
            {sections.map((section, index) => (
              <div
                key={section.id}
                draggable
                onDragStart={() => setDraggedId(section.id)}
                onDragEnd={() => setDraggedId(null)}
                onDragOver={(event: DragEvent<HTMLDivElement>) => event.preventDefault()}
                onDrop={() => { if (draggedId) moveTo(draggedId, section.id); setDraggedId(null); }}
                className={cn("group flex w-full cursor-grab items-center gap-2 rounded-2xl border p-2.5 text-left active:cursor-grabbing", selectedId === section.id ? "border-brand-300 bg-white shadow-sm" : "border-transparent hover:border-slate-200 hover:bg-white", draggedId === section.id && "opacity-40")}
              >
                <GripVertical size={16} className="shrink-0 text-slate-300" />
                <button type="button" onClick={() => setSelectedId(section.id)} className="min-w-0 flex-1 text-left">
                  <span className={cn("block truncate text-sm font-extrabold", section.enabled ? "text-slate-700" : "text-slate-400 line-through")}>{sectionLabel(section.type)}</span>
                  <span className="mt-0.5 block truncate text-[10px] font-semibold uppercase tracking-[.1em] text-slate-400">{section.variant || "default"}</span>
                </button>
                <span className="flex shrink-0 items-center gap-1">
                  <button type="button" onClick={(event: MouseEvent<HTMLButtonElement>) => { event.stopPropagation(); move(section.id, -1); }} className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-brand-700" aria-label={`Naikkan ${index + 1}`}><ChevronUp size={14} /></button>
                  <button type="button" onClick={(event: MouseEvent<HTMLButtonElement>) => { event.stopPropagation(); move(section.id, 1); }} className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-brand-700" aria-label={`Turunkan ${index + 1}`}><ChevronDown size={14} /></button>
                  <button type="button" onClick={(event: MouseEvent<HTMLButtonElement>) => { event.stopPropagation(); toggle(section.id); }} className={cn("relative h-5 w-9 rounded-full", section.enabled ? "bg-brand-500" : "bg-slate-300")} aria-label="Aktifkan section"><span className={cn("absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all", section.enabled ? "left-[18px]" : "left-0.5")} /></button>
                </span>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setLibraryOpen(true)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-brand-300 bg-brand-50 px-4 py-3 text-xs font-extrabold text-brand-700"><Plus size={15} /> Tambah section</button>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-xs font-black text-slate-700"><BadgeCheck size={15} className="text-brand-600" /> Builder aman</div>
            <p className="mt-2 text-xs leading-5 text-slate-500">Komponen memakai schema terstruktur. Pengguna tidak perlu mengedit HTML atau CSS.</p>
          </div>
        </aside>

        <section className="min-w-0 bg-[#eaf0ec]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
            <div className="flex items-center gap-2"><span className="inline-flex items-center gap-2 rounded-xl bg-brand-50 px-3 py-2 text-xs font-extrabold text-brand-800"><Eye size={15} /> Live preview</span><span className="hidden text-xs font-semibold text-slate-400 sm:inline">Revision {site?.revision}</span></div>
            <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 p-1">
              {[["desktop", Monitor, "Desktop"], ["tablet", Tablet, "Tablet"], ["mobile", Smartphone, "Mobile"]].map(([value, Icon, label]) => {
                const DeviceIcon = Icon as typeof Monitor;
                return <button key={value as string} type="button" onClick={() => setViewport(value as Viewport)} className={cn("grid h-8 w-9 place-items-center rounded-lg", viewport === value ? "bg-white text-brand-700 shadow-sm" : "text-slate-400")} aria-label={label as string}><DeviceIcon size={16} /></button>;
              })}
            </div>
          </div>
          <div className="hide-scrollbar flex h-[720px] overflow-auto p-4 sm:p-6">
            <div className={cn("mx-auto h-full w-full overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-2xl transition-all", widthClass)}>
              <iframe key={previewUrl} title="Preview website dari CSJW Sites" src={previewUrl} className="h-full w-full border-0 bg-white" />
            </div>
          </div>
        </section>

        <aside className="border-t border-slate-200 bg-white p-5 xl:border-l xl:border-t-0">
          <div className="flex items-start justify-between gap-3">
            <div><p className="text-xs font-black uppercase tracking-[0.16em] text-brand-700">Section terpilih</p><h3 className="mt-1 text-lg font-black text-slate-900">{selected ? sectionLabel(selected.type) : "-"}</h3><p className="mt-1 text-xs leading-5 text-slate-400">{selectedDefinition?.description || "Atur konten dan tampilan section."}</p></div>
            <div className="flex gap-1"><button type="button" onClick={duplicateSelected} className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-500 hover:text-brand-700" aria-label="Duplikasi"><Copy size={16} /></button><button type="button" onClick={deleteSelected} className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-500 hover:border-rose-200 hover:text-rose-600" aria-label="Hapus"><Trash2 size={16} /></button></div>
          </div>

          {selected && <div className="mt-6 space-y-6">
            {selectedDefinition && (
              <div>
                <span className="mb-3 block text-xs font-extrabold text-slate-600">Pilih gaya tampilan</span>
                <div className="grid gap-2">
                  {selectedDefinition.variants.map((variant) => <button key={variant.id} type="button" onClick={() => updateSelected({ variant: variant.id })} className={cn("rounded-2xl border p-3 text-left transition", selected.variant === variant.id ? "border-brand-300 bg-brand-50" : "border-slate-200 hover:border-brand-200")}><div className="flex items-center justify-between"><span className="text-sm font-extrabold text-slate-800">{variant.label}</span>{selected.variant === variant.id && <BadgeCheck size={16} className="text-brand-600" />}</div><p className="mt-1 text-[11px] leading-4 text-slate-500">{variant.description}</p></button>)}
                </div>
              </div>
            )}

            {(fieldMap[selected.type] || []).map((field) => (
              <label key={field.key} className="block">
                <span className="mb-2 block text-xs font-extrabold text-slate-600">{field.label}</span>
                {field.type === "textarea" ? <textarea className="field min-h-24 resize-y" value={String(selected.settings?.[field.key] || "")} placeholder={field.placeholder} onChange={(event: ChangeEvent<HTMLTextAreaElement>) => updateSelected({}, { [field.key]: event.target.value })} /> : <input className="field" value={String(selected.settings?.[field.key] || "")} placeholder={field.placeholder} onChange={(event: ChangeEvent<HTMLInputElement>) => updateSelected({}, { [field.key]: event.target.value })} />}
              </label>
            ))}

            {selected.type === "hero" && <div><span className="mb-2 block text-xs font-extrabold text-slate-600">Gambar hero</span><input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleHeroImage} /><button type="button" onClick={() => fileInputRef.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-xs font-extrabold text-slate-500 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"><ImageIcon size={18} /> {selected.settings?.imageUrl ? "Ganti gambar hero" : "Unggah gambar hero"}</button></div>}

            <div className="border-t border-slate-100 pt-5">
              <div className="flex items-center gap-2"><Palette size={16} className="text-brand-700" /><p className="text-xs font-extrabold text-slate-600">Tema website</p></div>
              <div className="mt-3 space-y-2">{themeChoices.map((theme) => <button key={theme.id} type="button" onClick={() => changeTheme(theme.id)} className={cn("flex w-full items-center gap-3 rounded-2xl border p-3 text-left", site?.theme?.id === theme.id ? "border-brand-300 bg-brand-50" : "border-slate-200")}><span className="flex -space-x-1">{theme.swatches.map((color) => <span key={color} className="h-6 w-6 rounded-full border-2 border-white shadow" style={{ background: color }} />)}</span><span className="min-w-0 flex-1"><span className="block text-xs font-extrabold text-slate-800">{theme.label}</span><span className="block truncate text-[10px] text-slate-400">{theme.description}</span></span>{site?.theme?.id === theme.id && <BadgeCheck size={15} className="text-brand-600" />}</button>)}</div>
            </div>

            <div className="rounded-2xl bg-slate-50 p-4"><div className="flex items-center gap-2 text-xs font-extrabold text-slate-700"><Laptop size={15} /> Satu renderer</div><p className="mt-2 text-xs leading-5 text-slate-500">Preview dan website live menggunakan project <code className="rounded bg-white px-1.5 py-0.5">csjw-sites</code> yang sama.</p></div>
          </div>}
        </aside>
      </div>

      {libraryOpen && <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/55 p-4 backdrop-blur-sm"><div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[2rem] bg-white shadow-2xl"><div className="flex items-center justify-between border-b border-slate-100 p-6"><div><p className="text-xs font-black uppercase tracking-[.16em] text-brand-700">Section library</p><h2 className="mt-1 text-2xl font-black text-slate-900">Pilih komponen siap pakai</h2></div><button type="button" onClick={() => setLibraryOpen(false)} className="grid h-10 w-10 place-items-center rounded-xl bg-slate-100 text-slate-500"><X size={18} /></button></div><div className="border-b border-slate-100 p-5"><div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"><label className="relative block flex-1"><Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input className="field pl-10" value={libraryQuery} onChange={(event: ChangeEvent<HTMLInputElement>) => setLibraryQuery(event.target.value)} placeholder="Cari hero, FAQ, statistik, ekspor..." /></label><div className="hide-scrollbar flex gap-2 overflow-x-auto">{["Semua", "Utama", "Konten", "Kepercayaan", "Konversi", "Ekspor"].map((category) => <button key={category} type="button" onClick={() => setLibraryCategory(category)} className={cn("whitespace-nowrap rounded-xl px-3 py-2 text-xs font-extrabold", libraryCategory === category ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-500")}>{category}</button>)}</div></div></div><div className="max-h-[60vh] overflow-y-auto p-5"><div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{filteredCatalog.map((definition) => { const locked = Boolean(definition.exportOnly && !account?.entitlements.exportTools); return <button key={definition.type} type="button" onClick={() => addSection(definition)} className={cn("group relative rounded-3xl border p-5 text-left transition", locked ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-soft")}><span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-50 text-brand-700">{locked ? <LockKeyhole size={20} /> : <LayoutGrid size={20} />}</span><h3 className="mt-4 text-base font-black text-slate-900">{definition.label}</h3><p className="mt-2 text-xs leading-5 text-slate-500">{definition.description}</p><div className="mt-4 flex flex-wrap gap-1.5">{definition.variants.slice(0, 3).map((variant) => <span key={variant.id} className="rounded-full bg-slate-100 px-2 py-1 text-[9px] font-black uppercase tracking-[.08em] text-slate-500">{variant.label}</span>)}</div>{definition.exportOnly && <span className="absolute right-4 top-4 rounded-full bg-accent-100 px-2 py-1 text-[9px] font-black text-accent-700">EXPORT</span>}</button>; })}</div></div></div></div>}

      {templateOpen && <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/55 p-4 backdrop-blur-sm"><div className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[2rem] bg-white shadow-2xl"><div className="flex items-center justify-between border-b border-slate-100 p-6"><div><p className="text-xs font-black uppercase tracking-[.16em] text-brand-700">Template website</p><h2 className="mt-1 text-2xl font-black text-slate-900">Mulai dari komposisi yang sudah matang</h2><p className="mt-2 text-sm text-slate-500">Produk, foto, dan identitas usaha tidak akan dihapus.</p></div><button type="button" onClick={() => setTemplateOpen(false)} className="grid h-10 w-10 place-items-center rounded-xl bg-slate-100 text-slate-500"><X size={18} /></button></div><div className="max-h-[70vh] overflow-y-auto p-6"><div className="grid gap-5 md:grid-cols-2">{builderTemplates.map((template) => { const locked = Boolean(template.exportOnly && !account?.entitlements.exportTools); return <article key={template.id} className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white"><div className={cn("relative h-40 overflow-hidden bg-gradient-to-br p-5 text-white", template.accent)}><div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/15 blur-2xl" /><span className="relative text-[10px] font-black uppercase tracking-[.18em] text-white/70">{template.eyebrow}</span><h3 className="relative mt-2 text-2xl font-black">{template.name}</h3><div className="relative mt-6 flex items-end gap-2">{template.sections.slice(0, 6).map((item, index) => <span key={`${item.type}-${index}`} className="block rounded-md bg-white/75" style={{ width: `${index % 2 === 0 ? 18 : 13}px`, height: `${30 + index * 7}px` }} />)}</div>{template.exportOnly && <span className="absolute right-4 top-4 rounded-full bg-white/15 px-3 py-1 text-[9px] font-black backdrop-blur">EXPORT</span>}</div><div className="p-5"><p className="text-sm leading-6 text-slate-500">{template.description}</p><div className="mt-4 flex flex-wrap gap-2">{template.sections.slice(0, 5).map((item, index) => <span key={`${item.type}-${index}`} className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-500">{sectionLabel(item.type)}</span>)}</div><button type="button" disabled={locked} onClick={() => applyTemplate(template)} className={cn("mt-5 flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold", locked ? "cursor-not-allowed bg-slate-100 text-slate-400" : "bg-brand-700 text-white hover:bg-brand-800")}>{locked ? <LockKeyhole size={16} /> : <Sparkles size={16} />} {locked ? "Aktifkan Siap Export" : "Gunakan template"}</button></div></article>; })}</div></div></div></div>}
    </div>
  );
}
